import React, { useEffect, useState, useContext, useRef } from "react";
import { getStockReport } from "../../../../Utility/Api";
import { useNavigate } from "react-router-dom";
import { getFormattedCurrentDate2, NaviGate, shop_id } from "../../../../Utility/function";
import { WarehouseContext } from "../../../../Utility/UserContext";
import "./../table.css";

const StockReport = () => {
    const navigate = useNavigate();
    const { showLoader, hideLoader, shopItem } = useContext(WarehouseContext);
    const contentRef = useRef(null);
    const [data, setData] = useState([]);
    const [filterType, setFilterType] = useState("daily");
    const [selectedDate, setSelectedDate] = useState(getFormattedCurrentDate2());
    const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
    const [selectedMonth, setSelectedMonth] = useState(getFormattedCurrentDate2().substring(0, 7));
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
    const [customFromDate, setCustomFromDate] = useState("");
    const [customToDate, setCustomToDate] = useState("");
    const [fromDate, setFromDate] = useState(getFormattedCurrentDate2());
    const [toDate, setToDate] = useState(getFormattedCurrentDate2());
    const [searchText, setSearchText] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [companyInfo, setCompanyInfo] = useState({
        name: "Your Company Name",
        address: "123 Business Street, City, State 12345",
        phone: "(123) 456-7890",
        email: "info@yourcompany.com"
    });

    useEffect(() => {
        if (shopItem) {
            setCompanyInfo({
                name: shopItem?.title || "",
                address: shopItem?.Address || "123 Business Street, City, State 12345",
                phone: shopItem?.phone1 || "",
                email: shopItem?.email || ""
            })
        }

    }, [shopItem])

    // Helper function to get current week range
    function getCurrentWeek() {
        const today = new Date();
        const firstDay = new Date(today.setDate(today.getDate() - today.getDay()));
        const lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 6));

        return {
            start: firstDay.toISOString().split('T')[0],
            end: lastDay.toISOString().split('T')[0]
        };
    }

    // Helper function to get week range from date
    function getWeekRange(dateString) {
        const date = new Date(dateString);
        const firstDay = new Date(date.setDate(date.getDate() - date.getDay()));
        const lastDay = new Date(date.setDate(date.getDate() - date.getDay() + 6));

        return {
            start: firstDay.toISOString().split('T')[0],
            end: lastDay.toISOString().split('T')[0]
        };
    }

    useEffect(() => {
        updateDateRange();
    }, [filterType, selectedDate, selectedWeek, selectedMonth, selectedYear, customFromDate, customToDate]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchData();
        }
    }, [fromDate, toDate]);

    const updateDateRange = () => {
        let startDate = "";
        let endDate = "";

        switch (filterType) {
            case "daily":
                startDate = selectedDate;
                endDate = selectedDate;
                break;
            case "weekly":
                startDate = selectedWeek.start;
                endDate = selectedWeek.end;
                break;
            case "monthly":
                startDate = `${selectedMonth}-01`;
                const lastDay = new Date(
                    new Date(startDate).getFullYear(),
                    new Date(startDate).getMonth() + 1,
                    0
                ).getDate();
                endDate = `${selectedMonth}-${lastDay}`;
                break;
            case "yearly":
                startDate = `${selectedYear}-01-01`;
                endDate = `${selectedYear}-12-31`;
                break;
            case "custom":
                startDate = customFromDate;
                endDate = customToDate;
                break;
            default:
                break;
        }

        setFromDate(startDate);
        setToDate(endDate);
    };

    const fetchData = async () => {
        try {
            showLoader();
            let filter = `start_date=${fromDate}&end_date=${toDate}&shop_id=${shop_id}`;
            const response = await getStockReport(filter);

            if (response.data && response.data.data) {
                // Calculate additional metrics for each product
                const enhancedData = response.data.data.map(item => {
                    const purchaseUnitPrice = item.total_purchase.price / (item.total_purchase.units || 1);
                    const salesUnitPrice = item.total_sales.price / (item.total_sales.units || 1);
                    const profitPerUnit = salesUnitPrice - purchaseUnitPrice;
                    const totalProfit = profitPerUnit * item.total_sales.units;
                    const profitMargin = salesUnitPrice > 0 ? (profitPerUnit / salesUnitPrice) * 100 : 0;

                    return {
                        ...item,
                        purchase_unit_price: purchaseUnitPrice,
                        sales_unit_price: salesUnitPrice,
                        profit_per_unit: profitPerUnit,
                        total_profit: totalProfit,
                        profit_margin: profitMargin
                    };
                });

                setData(enhancedData);
            } else {
                setData([]);
            }
            hideLoader();
        } catch (error) {
            hideLoader();
            console.error("Error fetching stock report:", error);
        }
    };

    const handleWeekChange = (dateString) => {
        setSelectedWeek(getWeekRange(dateString));
    };

    const handlePrint = () => {
        // Create a new window for printing
        const printWindow = window.open('', '_blank');

        // Get the printable content
        const printContent = document.getElementById('printable-report').innerHTML;

        // Write the content to the new window
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Stock Report</title>
                <style>
                    @media print {
                        @page {
                            size: A4 landscape;
                            margin: 0.5in;
                        }
                        body {
                            font-family: Arial, sans-serif;
                            font-size: 10pt;
                            color: #000;
                            background: #fff;
                        }
                        .print-container {
                            width: 100%;
                            max-width: 29.7cm;
                            margin: 0 auto;
                        }
                        .print-header {
                            text-align: center;
                            margin-bottom: 20px;
                            border-bottom: 2px solid #000;
                            padding-bottom: 15px;
                        }
                        .print-header h1 {
                            font-size: 18pt;
                            margin: 0;
                            color: #000;
                        }
                        .print-header p {
                            margin: 5px 0;
                            font-size: 10pt;
                        }
                        .report-info {
                            margin: 15px 0;
                            padding: 10px;
                            background-color: #f8f9fa;
                            border-radius: 5px;
                        }
                        .report-table {
                            width: 100%;
                            border-collapse: collapse;
                            margin: 15px 0;
                            font-size: 9pt;
                        }
                        .report-table th, .report-table td {
                            border: 1px solid #ddd;
                            padding: 6px;
                            text-align: left;
                        }
                        .report-table th {
                            background-color: #f2f2f2;
                            font-weight: bold;
                        }
                        .report-totals {
                            margin-top: 20px;
                            padding: 15px;
                            border: 1px solid #ddd;
                            border-radius: 5px;
                            background-color: #f9f9f9;
                        }
                        .total-row {
                            display: flex;
                            justify-content: space-between;
                            margin: 5px 0;
                        }
                        .total-amount {
                            font-weight: bold;
                            font-size: 11pt;
                        }
                        .text-success {
                            color: #28a745 !important;
                        }
                        .text-danger {
                            color: #dc3545 !important;
                        }
                        .footer {
                            margin-top: 30px;
                            text-align: center;
                            font-size: 9pt;
                            color: #6c757d;
                        }
                    }
                </style>
            </head>
            <body>
                ${printContent}
                <script>
                    // Print automatically when the window loads
                    window.onload = function() {
                        window.print();
                        // Close the window after printing
                        setTimeout(function() {
                            window.close();
                        }, 100);
                    };
                </script>
            </body>
            </html>
        `);

        printWindow.document.close();
    };

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const applyFilters = () => {
        let filtered = [...data];

        // Filter by search text
        if (searchText) {
            const search = searchText.toLowerCase();
            filtered = filtered.filter(item =>
                item.Product_details?.product_id?.toLowerCase().includes(search) ||
                item.Product_details?.product_name?.toLowerCase().includes(search)
            );
        }

        // Sort data
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                // Handle nested properties
                const getNestedValue = (obj, key) => {
                    return key.split('.').reduce((o, k) => (o || {})[k], obj);
                };

                const aValue = getNestedValue(a, sortConfig.key);
                const bValue = getNestedValue(b, sortConfig.key);

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        return filtered;
    };

    const filteredData = applyFilters();

    // Calculate summary statistics
    const summaryStats = filteredData.length > 0 ? {
        totalProducts: filteredData.length,
        totalPurchaseUnits: filteredData.reduce((sum, item) => sum + item.total_purchase.units, 0),
        totalSalesUnits: filteredData.reduce((sum, item) => sum + item.total_sales.units, 0),
        totalReturnUnits: filteredData.reduce((sum, item) => sum + item.total_purchase_return.units, 0),
        totalAvailableUnits: filteredData.reduce((sum, item) => sum + item.available_stocks.units, 0),
        totalPurchaseValue: filteredData.reduce((sum, item) => sum + item.total_purchase.price, 0),
        totalSalesValue: filteredData.reduce((sum, item) => sum + item.total_sales.price, 0),
        totalProfit: filteredData.reduce((sum, item) => sum + (item.total_profit || 0), 0)
    } : null;

    const renderFilterControls = () => {
        switch (filterType) {
            case "daily":
                return (
                    <div className="col-md-2">
                        <div className="form-floating">
                            <input
                                type="date"
                                className="form-control"
                                id="selectedDate"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                            />
                            <label htmlFor="selectedDate">Select Date</label>
                        </div>
                    </div>
                );
            case "weekly":
                return (
                    <div className="col-md-2">
                        <div className="form-floating">
                            <input
                                type="date"
                                className="form-control"
                                id="weekDate"
                                value={selectedWeek.start}
                                onChange={(e) => handleWeekChange(e.target.value)}
                            />
                            <label htmlFor="weekDate">Select Week</label>
                        </div>
                    </div>
                );
            case "monthly":
                return (
                    <div className="col-md-2">
                        <div className="form-floating">
                            <input
                                type="month"
                                className="form-control"
                                id="selectedMonth"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                            />
                            <label htmlFor="selectedMonth">Select Month</label>
                        </div>
                    </div>
                );
            case "yearly":
                return (
                    <div className="col-md-2">
                        <div className="form-floating">
                            <input
                                type="number"
                                className="form-control"
                                id="selectedYear"
                                min="2000"
                                max="2100"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                            />
                            <label htmlFor="selectedYear">Select Year</label>
                        </div>
                    </div>
                );
            case "custom":
                return (
                    <>
                        <div className="col-md-2">
                            <div className="form-floating">
                                <input
                                    type="date"
                                    className="form-control"
                                    id="customFromDate"
                                    value={customFromDate}
                                    onChange={(e) => setCustomFromDate(e.target.value)}
                                />
                                <label htmlFor="customFromDate">From Date</label>
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="form-floating">
                                <input
                                    type="date"
                                    className="form-control"
                                    id="customToDate"
                                    value={customToDate}
                                    onChange={(e) => setCustomToDate(e.target.value)}
                                />
                                <label htmlFor="customToDate">To Date</label>
                            </div>
                        </div>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <div className="page-wrappers">
            <div className="page-content py-1" ref={contentRef}>
                <NaviGate />

                {/* Filters Section */}
                <form className="card p-4 shadow-sm">
                    <div className="row g-3 justify-content-between align-items-center mb-3">
                        <h5 className="text-uppercase col-md-3">Stock Report</h5>

                        <div className="col-md-9 row g-3 justify-content-end">
                            {/* Filter Type Dropdown */}
                            <div className="col-md-2">
                                <div className="form-floating">
                                    <select
                                        className="form-select"
                                        id="filterType"
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                    >
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                        <option value="yearly">Yearly</option>
                                        <option value="custom">Custom Range</option>
                                    </select>
                                    <label htmlFor="filterType">Filter By</label>
                                </div>
                            </div>

                            {renderFilterControls()}

                            <div className="col-md-2">
                                <button type="button" className="btn btn-dark p-3 fw-semibold w-100" onClick={handlePrint}>
                                    Print Report
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Date Range Info */}
                    <div className="row mt-1">
                        <div className="col-md-12">
                            <div className="alert alert-secondary py-2">
                                <strong>Date Range:</strong> {fromDate} to {toDate} |
                                <strong> Total Records:</strong> {filteredData.length}
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="table-responsive text-center my-2 w-100 position-relative">
                        <table className="table table-sm table-bordered">
                            <thead className="table-dark">
                                <tr>
                                    <th onClick={() => handleSort('product_detail?.product_id')}>ID</th>
                                    <th onClick={() => handleSort('product_detail?.product_name')}>Product Name</th>
                                    <th onClick={() => handleSort('product_detail?.product_name')}>Warehouse</th>
                                    <th onClick={() => handleSort('total_purchase.units')}>Total Stocks ↕</th>
                                    <th onClick={() => handleSort('total_sales.units')}>Sold (Units) ↕</th>
                                    <th onClick={() => handleSort('total_purchase_return.units')}> Return ↕</th>
                                    <th onClick={() => handleSort('available_stocks.units')}>Available Stock ↕</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.length > 0 ? (
                                    <>
                                        {filteredData.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.Product_details?.product_id}</td>
                                                <td className="text-start">{item.Product_details?.product_name}</td>
                                                <td className="text-start">{item.Product_details?.ware_name}</td>
                                                <td>{item.total_purchase.units}</td>
                                                <td>{item.total_sales.units}</td>
                                                <td>{item.total_purchase_return.units}</td>
                                                <td className={item.available_stocks.units < 10 ? "text-danger fw-bold" : ""}>
                                                    {item.available_stocks.units}
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className="table-primary">
                                            <td colSpan={3} className="text-end fw-bold">TOTAL</td>
                                            <td className="fw-bold">{filteredData.reduce((sum, item) => sum + item.total_purchase.units, 0)}</td>
                                            <td className="fw-bold">{filteredData.reduce((sum, item) => sum + item.total_sales.units, 0)}</td>
                                            <td className="fw-bold">{filteredData.reduce((sum, item) => sum + item.total_purchase_return.units, 0)}</td>
                                            <td className="fw-bold">{filteredData.reduce((sum, item) => sum + item.available_stocks.units, 0)}</td>
                                        </tr>
                                    </>
                                ) : (
                                    <tr>
                                        <td colSpan="7">No data available</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </form>

                {/* Printable Report (Hidden from normal view) */}
                <div id="printable-report" style={{ display: 'none' }}>
                    <div className="print-container">
                        <div className="print-header">
                            <h1>{companyInfo.name}</h1>
                            <p>{companyInfo.address}</p>
                            <p>Phone: {companyInfo.phone} | Email: {companyInfo.email}</p>
                            <h2>Stock Report</h2>
                        </div>

                        <div className="report-info">
                            <p><strong>Report Period:</strong> {fromDate} to {toDate}</p>
                            <p><strong>Generated On:</strong> {new Date().toLocaleDateString()}</p>
                            <p><strong>Total Products:</strong> {filteredData.length}</p>
                        </div>

                        <table className="report-table">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Product ID</th>
                                    <th>Product Name</th>
                                    <th>Warehouse</th>
                                    <th>Total Stocks</th>
                                    <th>Sold Units</th>
                                    <th>Return Units</th>
                                    <th>Available Stock</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.length > 0 ? (
                                    filteredData.map((item, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{item.Product_details?.product_id}</td>
                                            <td>{item.Product_details?.product_name}</td>
                                            <td>{item.Product_details?.ware_name}</td>
                                            <td>{item.total_purchase.units}</td>
                                            <td>{item.total_sales.units}</td>
                                            <td>{item.total_purchase_return.units}</td>
                                            <td className={item.available_stocks.units < 10 ? "text-danger" : ""}>
                                                {item.available_stocks.units}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center' }}>
                                            No data available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {filteredData.length > 0 && (
                            <div className="report-totals">
                                <h3>Summary</h3>
                                <div className="total-row">
                                    <span>Total Products:</span>
                                    <span className="total-amount">{summaryStats.totalProducts}</span>
                                </div>
                                <div className="total-row">
                                    <span>Total Purchase Units:</span>
                                    <span className="total-amount">{summaryStats.totalPurchaseUnits}</span>
                                </div>
                                <div className="total-row">
                                    <span>Total Sales Units:</span>
                                    <span className="total-amount">{summaryStats.totalSalesUnits}</span>
                                </div>
                                <div className="total-row">
                                    <span>Total Return Units:</span>
                                    <span className="total-amount">{summaryStats.totalReturnUnits}</span>
                                </div>
                                <div className="total-row">
                                    <span>Total Available Units:</span>
                                    <span className="total-amount">{summaryStats.totalAvailableUnits}</span>
                                </div>
                            </div>
                        )}

                        <div className="footer">
                            <p>Report generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StockReport;